import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_NINJA } from '../../settings';
import { StatusEnum } from '../common-types';

export interface IKeyword {
  id: number;
  owner: number;
  weight: number;
  keyword: string;
  is_active: boolean;
  added?: string;
}

export interface IKeywordGroup {
  id: number;
  project: number;
  name: string;
  description: string;
  is_active: boolean;
  keywords: IKeyword[];
  created: string;
  changed: string;
  pages: number;
}

export interface INewKeywordGroup {
  project: number;
  name: string;
  description: string;
  is_active: boolean;
  new_keywords: Array<string>;
}

export interface IUpdateKeywordGroup {
  id: number;
  name?: string;
  description?: string;
  is_active?: boolean;
  new_keywords?: Array<string>;
  pages?: number;
}

export interface IKeywordGroupsState {
  keywordGroups: IKeywordGroup[];
  currentKeywordGroup: IKeywordGroup | null;
  status: StatusEnum;
  statusText: string | null;
}

const initialState: IKeywordGroupsState = {
  keywordGroups: [],
  currentKeywordGroup: null,
  status: StatusEnum.idle,
  statusText: null,
};

export const getKeywordGroups = createAsyncThunk(
  'organization/getKeywordGroups',
  async (args: { slug: string; clientId?: number; projectId?: number }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups`,
      { params }
    );
    return response.data as IKeywordGroup[];
  }
);

export const getKeywordGroup = createAsyncThunk(
  'organization/getKeywordGroup',
  async (args: {
    slug: string;
    keywordGroupId: number;
    clientId?: number;
    projectId?: number;
  }) => {
    const params: { client?: number; project?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    if (args.projectId) {
      params.project = args.projectId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups/${args.keywordGroupId}`,
      { params }
    );
    return response.data as IKeywordGroup;
  }
);

export const createKeywordGroup = createAsyncThunk(
  'organization/createKeywordGroup',
  async (args: { slug: string; keywordGroup: INewKeywordGroup }) => {
    const response = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups`,
      args.keywordGroup
    );
    return response.data as IKeywordGroup;
  }
);

export const updateKeywordGroup = createAsyncThunk(
  'organization/updateKeywordGroup',
  async (args: { slug: string; keywordGroup: IUpdateKeywordGroup }) => {
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups/${args.keywordGroup.id}`,
      args.keywordGroup
    );
    return response.data as IKeywordGroup;
  }
);

export const deleteKeywordGroup = createAsyncThunk(
  'organization/deleteKeywordGroup',
  async (args: { slug: string; keywordGroupId: number }) => {
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups/${args.keywordGroupId}`
    );
    return args.keywordGroupId;
  }
);

export const updateKeyword = createAsyncThunk(
  'organization/updateKeyword',
  async (args: {
    slug: string;
    keywordGroupId: number;
    keywordId: number;
    data: { keyword?: string; weight?: number; is_active?: boolean };
  }) => {
    const response = await axios.patch(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/keyword_groups/${args.keywordGroupId}/keywords/${args.keywordId}`,
      args.data
    );
    return {
      keywordGroupId: args.keywordGroupId,
      keyword: response.data as IKeyword,
    };
  }
);

const keywordGroupsSlice = createSlice({
  name: 'keywordGroups',
  initialState,
  reducers: {
    setKeywordGroup: (state, action: PayloadAction<IKeywordGroup | null>) => {
      state.currentKeywordGroup = action.payload;
    },
    setKeywordGroupIdle: (state) => {
      state.status = StatusEnum.idle;
      state.statusText = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKeywordGroups.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getKeywordGroups.fulfilled,
        (state, action: PayloadAction<IKeywordGroup[]>) => {
          state.status = StatusEnum.succeeded;
          state.keywordGroups = action.payload;
          if (!state.currentKeywordGroup) {
            state.currentKeywordGroup = action.payload[0] ?? null;
          }
        }
      )
      .addCase(getKeywordGroups.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(getKeywordGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getKeywordGroup.fulfilled,
        (state, action: PayloadAction<IKeywordGroup>) => {
          state.status = StatusEnum.succeeded;
          state.currentKeywordGroup = action.payload;
        }
      )
      .addCase(getKeywordGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(createKeywordGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        createKeywordGroup.fulfilled,
        (state, action: PayloadAction<IKeywordGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа фраз создана';
          state.currentKeywordGroup = action.payload;
          state.keywordGroups.push(action.payload);
          toast.success(state.statusText);
        }
      )
      .addCase(createKeywordGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateKeywordGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateKeywordGroup.fulfilled,
        (state, action: PayloadAction<IKeywordGroup>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа фраз обновлена';
          const updatedKeywordGroupIndex = state.keywordGroups.findIndex(
            (c) => c.id === action.payload.id
          );
          if (updatedKeywordGroupIndex !== -1) {
            state.keywordGroups[updatedKeywordGroupIndex] = action.payload;
          } else {
            state.currentKeywordGroup = null;
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateKeywordGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(deleteKeywordGroup.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteKeywordGroup.fulfilled,
        (state, action: PayloadAction<number | undefined>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Группа фраз удалена';
          if (typeof action.payload !== 'undefined') {
            const deletedKeywordGroupIndex = state.keywordGroups.findIndex(
              (c) => c.id === action.payload
            );
            if (deletedKeywordGroupIndex >= 0) {
              state.keywordGroups.splice(deletedKeywordGroupIndex, 1);
            }
          }
          state.currentKeywordGroup = null;
          toast.success(state.statusText);
        }
      )
      .addCase(deleteKeywordGroup.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      .addCase(updateKeyword.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateKeyword.fulfilled,
        (
          state,
          action: PayloadAction<{
            keywordGroupId: number;
            keyword: IKeyword;
          }>
        ) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Ключевое слово обновлено';
          const groupIndex = state.keywordGroups.findIndex(
            (g) => g.id === action.payload.keywordGroupId
          );
          if (groupIndex !== -1) {
            const group = state.keywordGroups[groupIndex];
            const keywords = group?.keywords;
            if (keywords) {
              const keywordIndex = keywords.findIndex(
                (k) => k?.id === action.payload.keyword.id
              );
              if (keywordIndex !== -1 && keywordIndex !== undefined) {
                keywords[keywordIndex] = action.payload.keyword;
              }
            }
          }
          toast.success(state.statusText);
        }
      )
      .addCase(updateKeyword.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      });
  },
});

export const { setKeywordGroup, setKeywordGroupIdle } =
  keywordGroupsSlice.actions;

export default keywordGroupsSlice.reducer;
